import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';
import type { PropsWithChildren } from 'react';

import type { Event, GeneralEvent } from '@vlabs/api/luna-api/model';

export type WorkspaceEvent = Event | GeneralEvent;

interface WorkspaceEventContextValue {
  event: WorkspaceEvent | null;
  setEvent: (event: WorkspaceEvent) => void;
  clearEvent: () => void;
}

const WorkspaceEventContext = createContext<WorkspaceEventContextValue | undefined>(undefined);

export function useWorkspaceEventContext(): WorkspaceEventContextValue {
  const context = useContext(WorkspaceEventContext);

  if (context === undefined) {
    throw new Error('Context must be used with WorkspaceEventProvider');
  }

  return context;
}

export function WorkspaceEventProvider({
  children = undefined,
}: PropsWithChildren): React.JSX.Element {
  const [event, setEvent] = useState<WorkspaceEvent | null>(null);

  const clearEvent = useCallback((): void => {
    setEvent(null);
  }, []);

  const contextValue = useMemo<WorkspaceEventContextValue>(() => ({
    event,
    setEvent,
    clearEvent,
  }), [clearEvent, event]);

  return (
    <WorkspaceEventContext.Provider value={contextValue}>
      {children}
    </WorkspaceEventContext.Provider>
  );
}

WorkspaceEventProvider.displayName = 'WorkspaceEventProvider';
